// @flow
import { FormFileUpload } from 'components/molecules/formFileUpload';
import React from 'react';
import { Form, FormikProps, Formik, FormikHelpers, FormikValues } from 'formik';
import * as Yup from 'yup';
import cogoToast from 'cogo-toast';
import api from 'apiClient/api';
import { StringUtil } from 'utils/StringUtil';
import { FormButton } from 'components/molecules/formButton';
import { FormInput } from 'components/molecules/formInput';
import { FormTextArea } from 'components/molecules/formTextArea';
// eslint-disable-next-line
import styles from "./styles.scss";

const API_ENDPOINT = 'sendJobApplication.json';
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

type Props = {
  onSubmitSuccess: () => void,
  isMobile: boolean,
  roleName?: string,
  jobEmail?: string,
};

type FormState = {
  fullName: string,
  emailAddress: string,
  phoneNumber: string,
  location: string,
  coverLetter: string,
  resume: any,
  remarks: string,
};

type State = {
  data: FormState,
};

const getInitialState = (): State => {
  return {
    data: {
      fullName: '',
      emailAddress: '',
      phoneNumber: '',
      location: '',
      coverLetter: '',
      resume: undefined,
      remarks: '',
      roleName: '',
    },
  };
};

export class JobApplicationForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { ...getInitialState() };
  }

  render = () => {
    const buttonMode = this.props.isMobile ? 'transparent' : 'dark';

    return (
      <div className={'job-application-form'}>
        <Formik
          initialValues={this.state.data}
          onSubmit={this.onSubmit}
          validationSchema={this.getSchema()}
        >
          {(props: FormikProps<State>) => (
            <Form>
              <FormFileUpload
                id={'job-application-resume'}
                label={'Résumé'}
                name={'resume'}
                value={props.values['resume'] ? [props.values['resume']] : []}
                accept={ALLOWED_MIME_TYPES}
              />
              <FormInput
                id={'job-application-full-name'}
                name={'fullName'}
                label={'Full Name'}
                placeholder={'Full Name'}
                type={'text'}
                value={props.values['fullName']}
              />
              <FormInput
                id={'job-application-email-address'}
                name={'emailAddress'}
                label={'Email Address'}
                placeholder={'Email Address'}
                type={'email'}
                value={props.values['emailAddress']}
              />
              <FormInput
                id={'job-application-phone-number'}
                name={'phoneNumber'}
                label={'Phone Number'}
                placeholder={'Phone Number'}
                value={props.values['phoneNumber']}
                type={'number'}
              />
              <FormInput
                id={'job-application-location'}
                name={'location'}
                label={'Location'}
                placeholder={'Location'}
                value={props.values['location']}
              />
              <FormTextArea
                id={'job-application-cover-letter'}
                name={'coverLetter'}
                label={'Cover Letter'}
                placeholder={'Cover Letter'}
                value={props.values['coverLetter']}
                rows={2}
              />
              <FormTextArea
                id={'job-application-remarks'}
                name={'remarks'}
                label={'Remarks'}
                placeholder={'Remarks'}
                value={props.values['remarks']}
                rows={2}
              />
              <FormButton disabled={props.isSubmitting} mode={buttonMode} />
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  onSubmit = (values: FormikValues, actions: FormikHelpers<FormikValues>) => {
    actions.setSubmitting(true);
    const { hide: hideLoader } = cogoToast.loading(
      'Submitting your information...',
      {
        hideAfter: 0,
      }
    );
    values['jobEmail'] = this.props.jobEmail;
    values['job_role'] = this.props.roleName;
    let payload = this.constructPayload(values);

    const config = {
      // 'content-type': 'multipart/form-data',
      // Authorization: '',
      ifFormData: true,
    };
    api
      .post(API_ENDPOINT, payload, config)
      .then(res => {
        hideLoader();
        setTimeout(() => {
          cogoToast.success('Submitted your information successfully!');
          this.props.onSubmitSuccess && this.props.onSubmitSuccess();
        }, 300);
        actions.resetForm({ ...getInitialState() });
      })
      .catch(error => {
        hideLoader();
        setTimeout(() => {
          cogoToast.error('Failed to submit your information');
        }, 300);
      })
      .finally(() => {
        window.location.href = '/thank-you';
        actions.setSubmitting(false);
      });
  };

  constructPayload = (values: FormState): FormData => {
    const formData = new FormData();
    Object.keys(values).forEach(key => {
      let value = values[key];
      if (key !== 'resume') {
        value = value.toString().trim();
      }
      if (key === 'roleName') {
        value = this.props.roleName;
      }
      formData.append(key, value);
    });
    return formData;
  };

  getSchema = () => {
    const schema = {
      resume: Yup.mixed().required('Please upload your Résumé'),
      fullName: Yup.string()
        .trim()
        .required('Please enter your Full Name'),
      emailAddress: Yup.string()
        .trim()
        .email('Please enter a valid email')
        .required('Please enter your Email address'),
      phoneNumber: Yup.string()
        .trim()
        .matches(
          StringUtil.regexPatterns.phone,
          'Please enter a valid phone number'
        )
        .required('Please enter your Phone number'),
      location: Yup.string()
        .trim()
        .required('Please enter your Location'),
      remarks: Yup.string()
        .trim()
        .max(255, 'Please enter a shorter message'),
    };

    return Yup.object().shape(schema);
  };
}
